import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminEventsService } from '../services/admin.events.service'
import type { EventDocument } from '@/types'
import type { EventFormValues } from '../types'
import { useAuth } from '@/hooks/useAuth'

interface UseAdminEventsResult {
  events: Array<EventDocument & { id: string }>
  loading: boolean
  error: string | null
  createEvent: (data: EventFormValues) => Promise<string>
  updateEvent: (id: string, data: Partial<EventFormValues>) => Promise<void>
  deleteEvent: (id: string) => Promise<void>
  refresh: () => void
}

export function useAdminEvents(): UseAdminEventsResult {
  const { user } = useAuth()
  const [events, setEvents] = useState<Array<EventDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => {
    setTick((t) => t + 1)
  }, [])

  useEffect(() => {
    setLoading(true)
    setError(null)
    adminEventsService
      .getAllEvents()
      .then(setEvents)
      .catch((err: unknown) =>
        setError(formatUserFacingError(err, 'Failed to load events')),
      )
      .finally(() => setLoading(false))
  }, [tick])

  const createEvent = useCallback(
    async (data: EventFormValues): Promise<string> => {
      const createdBy = user?.uid ?? 'unknown'
      const id = await adminEventsService.createEvent(data, createdBy)
      refresh()
      return id
    },
    [refresh, user],
  )

  const updateEvent = useCallback(
    async (id: string, data: Partial<EventFormValues>): Promise<void> => {
      await adminEventsService.updateEvent(id, data)
      refresh()
    },
    [refresh],
  )

  const deleteEvent = useCallback(
    async (id: string): Promise<void> => {
      await adminEventsService.deleteEvent(id)
      refresh()
    },
    [refresh],
  )

  return { events, loading, error, createEvent, updateEvent, deleteEvent, refresh }
}
